import React, { useEffect, useState, useRef, MutableRefObject, ReactNode } from 'react'
import { ReactComponent as AgencyIcon } from '../images/icons/agency_icon_black.svg'
import { useDealership } from '../store/zustand/dealershipStore'
import { ChevronRight } from '@demandlocal/components'
import {
  useInteractions,
  arrow,
  autoUpdate,
  shift,
  useFloating,
} from '@floating-ui/react'
import { Link, useNavigate } from 'react-router-dom'
import { ReactComponent as IconChart } from '../images/icons/icon_chart.svg'
import { ReactComponent as ClientIcon } from '../images/icons/client_icon.svg'
import { ReactComponent as UsersIcon } from '../images/icons/users_icon.svg'
import { ReactComponent as InvoicesIcon } from '../images/icons/invoices.svg'
import { ReactComponent as CampaignsIcon } from '../images/icons/cars_multiple.svg'
import { ReactComponent as ReportsIcon } from '../images/icons/report_icon.svg'
import { ReactComponent as LinkedPlatformsIcon } from '../images/icons/linked_accounts.svg'
import { useTranslation } from 'react-i18next'

const Menu = (props: JSX.IntrinsicElements['div']): JSX.Element => {
  return (
    <div
      className="bg-white rounded absolute z-[2] mt-2 shadow-[0_0_0_1px_hsla(218, 50%, 10%, 0.1),0_4px_11px_hsla(218, 50%, 10%, 0.1)]"
      {...props}
    />
  )
}
const Blanket = (props: JSX.IntrinsicElements['div']): JSX.Element => (
  <div className="fixed bottom-0 left-0 top-0 right-0 z-[1]" {...props} />
)

type FlyoutProps = {
  isOpen: boolean
  target: ReactNode
  onClose: () => void
  children: JSX.Element | false
}

const Flyout = ({ children, isOpen, target, onClose }: FlyoutProps): JSX.Element => (
  <div>
    {target}
    {isOpen ? <Menu>{children}</Menu> : null}
    {isOpen ? <Blanket onClick={onClose} /> : null}
  </div>
)

export default function AgencyFlyout(): JSX.Element {
  const currentDealership = useDealership()

  const [isOpen, setIsOpen] = useState(false)

  const arrowRef = useRef(null)
  const { refs, strategy, x, y } = useFloating({
    placement: 'right-start',
    middleware: [shift(), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  })

  const { getFloatingProps } = useInteractions()
  const dropdownRef: MutableRefObject<HTMLDivElement | null> = useRef(null)

  const handleClickOutside = (event): void => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target))
      setIsOpen(false)
  }

  useEffect(() => {
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    else document.removeEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const { t } = useTranslation('routes')
  const navigate = useNavigate()

  const menuOptions = [
    {
      to: t('agency.profile.overview', { agency_id: currentDealership?.organization_id }),
      icon: <IconChart className="fill-web-gray group-hover:fill-white" />,
      label: 'Overview',
    },
    {
      to: t('agency.profile.clients', { agency_id: currentDealership?.organization_id }),
      icon: <ClientIcon className="stroke-web-gray group-hover:stroke-white" />,
      label: 'Clients',
    },
    {
      to: t('agency.profile.campaigns.list', {
        agency_id: currentDealership?.organization_id,
      }),
      icon: <CampaignsIcon className="stroke-web-gray group-hover:stroke-white" />,
      label: 'Campaigns',
    },
    {
      to: t('agency.profile.users', { agency_id: currentDealership?.organization_id }),
      icon: <UsersIcon className="stroke-web-gray group-hover:stroke-white" />,
      label: 'Users',
    },
    {
      to: t('agency.profile.invoices', { agency_id: currentDealership?.organization_id }),
      icon: <InvoicesIcon className="ml-1 fill-web-gray group-hover:fill-white" />,
      label: 'Invoices',
    },
    {
      to: t('agency.profile.reports', { agency_id: currentDealership?.organization_id }),
      icon: <ReportsIcon className="fill-web-gray group-hover:fill-white" />,
      label: 'Reports',
    },
    {
      to: t('agency.profile.accounts', { agency_id: currentDealership?.organization_id }),
      icon: (
        <LinkedPlatformsIcon className="stroke-web-gray fill-web-gray group-hover:fill-white group-hover:stroke-white" />
      ),
      label: 'Linked Platforms',
    },
  ] as Array<{ to: string; icon: JSX.Element; label: string }>

  return (
    <Flyout
      isOpen={isOpen}
      onClose={() => setIsOpen((prev) => !prev)}
      target={
        <div
          className="flex flex-row items-center gap-2 p-5 border-b-2 border-space-cadet cursor-pointer"
          ref={refs.setReference}
          onClick={(e) => setIsOpen(true)}>
          <div className="flex justify-center items-center bg-white min-w-8 min-h-8 rounded-full">
            <AgencyIcon className="w-5 h-5" />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-primary-glaucous text-[10px] font-bold uppercase">
              Agency
            </span>
            <span className="text-white font-bold text-xs w-full">
              {currentDealership?.organization_name}
            </span>
          </div>
          <div className="ml-auto w-6 h-6 rounded-full bg-space-cadet">
            {/* @ts-ignore */}
            <ChevronRight className="stroke-white fill-white" />
          </div>
        </div>
      }>
      {isOpen && (
        <div
          ref={(ref) => {
            refs.setFloating(ref)
            dropdownRef.current = ref
          }}
          className="absolute bg-blue-primary p-5 rounded-lg"
          {...getFloatingProps({
            style: {
              position: strategy,
              top: y ? y - 10 : 0,
              left: x ? x + 12 : 0,
            },
          })}>
          <div className="flex flex-col gap-4 w-full w-[140px]">
            {menuOptions.map((m) => (
              <div
                key={m.label}
                className="flex flex-row gap-2 items-center w-[140px] group cursor-pointer"
                onClick={(e) => {
                  navigate(m.to)
                  setIsOpen(false)
                }}>
                {m.icon}
                <span className="font-bold text-xs text-web-gray group-hover:text-white">
                  {m.label}
                </span>
              </div>
            ))}
          </div>
          <div
            className="w-4 h-4 bg-blue-primary [left:-0.5rem] top-[40.5px] absolute transition transition-transform duration-200 ease-in-out rotate-45"
            ref={arrowRef}
          />
        </div>
      )}
    </Flyout>
  )
}
